import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  CurrencyRupeeIcon,
  DocumentArrowUpIcon,
  FunnelIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PencilIcon,
  PlusIcon,
  ReceiptPercentIcon,
  TagIcon,
  TrashIcon,
  TruckIcon,
  UserGroupIcon,
  WifiIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { toast } from 'sonner';
import { usePageMeta } from '../lib/pageMeta';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const tok = () => localStorage.getItem('admin_token');
const authHeader = () => ({ Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' });

const apiFetch = (url, opts = {}) =>
  fetch(`${API}${url}`, { headers: authHeader(), ...opts }).then(async (r) => {
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || 'Request failed');
    return j;
  });

// ── Category config ──────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'electricity',    label: 'Electricity Bills',           icon: '⚡', color: '#f59e0b' },
  { value: 'printing',       label: 'Paper & Printing',            icon: '🖨️', color: '#6366f1' },
  { value: 'marketing',      label: 'Advertising & Marketing',     icon: '📢', color: '#ec4899' },
  { value: 'infrastructure', label: 'Infrastructure & Maintenance',icon: '🏗️', color: '#14b8a6' },
  { value: 'salaries',       label: 'Staff Salaries',              icon: '👥', color: '#3b82f6' },
  { value: 'trainer',        label: 'Trainer Payments',            icon: '🎓', color: '#8b5cf6' },
  { value: 'batch_specific', label: 'Batch Expenses',              icon: '📋', color: '#10b981' },
  { value: 'internet',       label: 'Internet & Communication',    icon: '🌐', color: '#06b6d4' },
  { value: 'office_supplies',label: 'Office Supplies',             icon: '📎', color: '#f97316' },
  { value: 'software',       label: 'Software & Subscriptions',    icon: '💻', color: '#7c3aed' },
  { value: 'rent',           label: 'Rent & Utilities',            icon: '🏢', color: '#0ea5e9' },
  { value: 'travel',         label: 'Travel & Conveyance',         icon: '🚗', color: '#84cc16' },
  { value: 'miscellaneous',  label: 'Miscellaneous',               icon: '📦', color: '#6b7280' },
  { value: 'custom',         label: 'Custom Category',             icon: '✏️', color: '#d97706' },
];

const PAYMENT_METHODS = [
  { value: 'cash',            label: 'Cash' },
  { value: 'cheque',          label: 'Cheque' },
  { value: 'online_transfer', label: 'Online Transfer' },
  { value: 'upi',             label: 'UPI' },
  { value: 'card',            label: 'Card' },
  { value: 'other',           label: 'Other' },
];

const PIE_COLORS = CATEGORIES.map(c => c.color);

const catInfo = (val) => CATEGORIES.find(c => c.value === val) || { label: val, icon: '📦', color: '#6b7280' };

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── BLANK form ───────────────────────────────────────────────────────────────
const BLANK = {
  title: '', category: 'electricity', customCategory: '',
  amount: '', date: new Date().toISOString().slice(0, 10),
  description: '', paymentMethod: 'cash',
  paymentSlip: '', batchId: '', courseId: '',
  staffId: '', trainerId: '',
};

// ── Small components ─────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, bg, text }) => (
  <div className={`rounded-2xl border p-5 flex items-start gap-4 ${bg}`}>
    <div className={`text-3xl`}>{icon}</div>
    <div className="min-w-0">
      <p className={`text-xs font-semibold uppercase tracking-wider ${text} opacity-70`}>{label}</p>
      <p className={`text-2xl font-bold ${text} mt-0.5`}>{value}</p>
      {sub && <p className={`text-xs mt-0.5 ${text} opacity-60`}>{sub}</p>}
    </div>
  </div>
);

const Badge = ({ children, color = 'gray' }) => {
  const cls = {
    gray:   'bg-gray-100 text-gray-700',
    blue:   'bg-blue-100 text-blue-700',
    green:  'bg-green-100 text-green-700',
    amber:  'bg-amber-100 text-amber-700',
    violet: 'bg-violet-100 text-violet-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls[color] || cls.gray}`}>
      {children}
    </span>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function ExpenseTracker() {
  usePageMeta({ title: 'Expense Tracker | Emax Technologies', description: 'Track all institute expenses' });

  const [expenses,  setExpenses]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [batches,   setBatches]   = useState([]);
  const [courses,   setCourses]   = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [trainers,  setTrainers]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [total,     setTotal]     = useState(0);

  // Filters (draft = in panel, applied = sent to API)
  const BLANK_DRAFT = { search: '', category: 'all', batchId: 'all', startDate: '', endDate: '' };
  const [draft,    setDraft]    = useState(BLANK_DRAFT);
  const [applied,  setApplied]  = useState(BLANK_DRAFT);
  const [filterOpen, setFilterOpen] = useState(false);

  // Add / Edit dialog
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editId,     setEditId]       = useState(null);
  const [form,       setForm]         = useState(BLANK);
  const [saving,     setSaving]       = useState(false);
  const [slipUploading, setSlipUploading] = useState(false);
  const slipInputRef = useRef();

  // Delete
  const [deleteId,  setDeleteId]  = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'charts'

  // ── Load reference data once ─────────────────────────────────────────────
  useEffect(() => {
    apiFetch('/analytics/batches').then(r => setBatches(r.data || [])).catch(() => {});
    apiFetch('/student-dashboard/courses').then(r => setCourses(r.data?.data || r.data || [])).catch(() => {});
    apiFetch('/analytics/staff-list').then(r => setStaffList(r.data || [])).catch(() => {});
    apiFetch('/trainers?limit=200&isActive=true').then(r => setTrainers(r.trainers || r.data || [])).catch(() => {});
  }, []);

  // ── Fetch expenses when applied filters change ───────────────────────────
  useEffect(() => { fetchExpenses(); }, [applied]); // eslint-disable-line

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '200' });
      if (applied.search)   p.set('search',    applied.search);
      if (applied.category && applied.category !== 'all') p.set('category', applied.category);
      if (applied.batchId  && applied.batchId  !== 'all') p.set('batchId',  applied.batchId);
      if (applied.startDate) p.set('startDate', applied.startDate);
      if (applied.endDate)   p.set('endDate',   applied.endDate);
      const r = await apiFetch(`/expenses?${p}`);
      setExpenses(r.data || []);
      setTotal(r.total || 0);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const p = new URLSearchParams();
      if (applied.startDate) p.set('startDate', applied.startDate);
      if (applied.endDate)   p.set('endDate',   applied.endDate);
      if (applied.batchId && applied.batchId !== 'all') p.set('batchId', applied.batchId);
      const r = await apiFetch(`/expenses/stats?${p}`);
      setStats(r.data);
    } catch {}
    finally { setStatsLoading(false); }
  };

  useEffect(() => { if (activeTab === 'charts') fetchStats(); }, [activeTab, applied]); // eslint-disable-line

  // ── Handlers ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditId(null);
    setForm({ ...BLANK, date: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };

  const openEdit = (exp) => {
    setEditId(exp._id);
    setForm({
      title:          exp.title,
      category:       exp.category,
      customCategory: exp.customCategory || '',
      amount:         exp.amount,
      date:           exp.date?.slice(0, 10) || '',
      description:    exp.description || '',
      paymentMethod:  exp.paymentMethod || 'cash',
      paymentSlip:    exp.paymentSlip   || '',
      batchId:        exp.batchId?._id   || exp.batchId   || '',
      courseId:       exp.courseId?._id  || exp.courseId  || '',
      staffId:        exp.staffId?._id   || exp.staffId   || '',
      trainerId:      exp.trainerId?._id || exp.trainerId || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.date) { toast.error('Date is required'); return; }
    if (form.category === 'custom' && !form.customCategory.trim()) { toast.error('Custom category name is required'); return; }

    setSaving(true);
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (!body.batchId)   delete body.batchId;
      if (!body.courseId)  delete body.courseId;
      if (!body.staffId)   delete body.staffId;
      if (!body.trainerId) delete body.trainerId;

      if (editId) {
        await apiFetch(`/expenses/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
        toast.success('Expense updated');
      } else {
        await apiFetch('/expenses', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Expense added');
      }
      setDialogOpen(false);
      fetchExpenses();
      if (activeTab === 'charts') fetchStats();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await apiFetch(`/expenses/${deleteId}`, { method: 'DELETE' });
      toast.success('Expense deleted');
      setDeleteId(null);
      fetchExpenses();
      if (activeTab === 'charts') fetchStats();
    } catch (e) { toast.error(e.message || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleSlipUpload = async (file) => {
    if (!file) return;
    setSlipUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/storage/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok()}` },
        body: fd,
      });
      const data = await r.json();
      if (data.url) {
        setForm(f => ({ ...f, paymentSlip: data.url }));
        toast.success('Slip uploaded');
      }
    } catch { toast.error('Upload failed'); }
    finally { setSlipUploading(false); }
  };

  const handleExport = () => {
    if (!expenses.length) return;
    const headers = ['Date','Title','Category','Amount (₹)','Payment Method','Batch','Course','Description','Slip'];
    const rows = expenses.map(e => [
      formatDateForCsv(e.date),
      e.title,
      e.category === 'custom' ? e.customCategory : catInfo(e.category).label,
      e.amount,
      e.paymentMethod,
      e.batchId?.name  || '-',
      e.courseId?.name || '-',
      e.description    || '-',
      e.paymentSlip    || '-',
    ]);
    downloadCsv(headers, rows, `expenses-${new Date().toISOString().slice(0,10)}.csv`);
    toast.success(`Exported ${rows.length} records`);
  };

  const activeFilterCount = [
    applied.search,
    applied.category !== 'all',
    applied.batchId  !== 'all',
    applied.startDate,
    applied.endDate,
  ].filter(Boolean).length;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30 p-4 sm:p-6 space-y-6 font-sans text-sm">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ReceiptPercentIcon className="h-7 w-7 text-violet-600" />
            Expense Tracker
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Track, categorise and analyse all institute expenses</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 shadow-sm transition-colors"
          >
            <FunnelIcon className="h-4 w-4 text-indigo-500" />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 shadow-sm transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold shadow transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Expense
          </button>
        </div>
      </div>

      {/* ── Quick Stats Row ── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon="💸" label="Total Expenses" value={fmt(stats.totalAmount)} sub={`${stats.totalCount} entries`} bg="bg-violet-50 border-violet-200" text="text-violet-800" />
          {stats.byCategory.slice(0,3).map((c, i) => {
            const ci = catInfo(c.category);
            const bgs = ['bg-blue-50 border-blue-200','bg-emerald-50 border-emerald-200','bg-amber-50 border-amber-200'];
            const txs = ['text-blue-800','text-emerald-800','text-amber-800'];
            return <StatCard key={c.category} icon={ci.icon} label={ci.label} value={fmt(c.total)} sub={`${c.count} entries`} bg={bgs[i]} text={txs[i]} />;
          })}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { id: 'list',   label: 'Expense List',  icon: ClipboardDocumentListIcon },
          { id: 'charts', label: 'Analytics',      icon: ChartBarIcon },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === t.id ? 'bg-white shadow text-violet-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ LIST TAB ══ */}
      {activeTab === 'list' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="font-semibold text-gray-800">
              {total} {total === 1 ? 'expense' : 'expenses'}{activeFilterCount > 0 ? ' (filtered)' : ''}
            </p>
          </div>

          {loading ? (
            <div className="divide-y divide-gray-50">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-gray-100 rounded-full w-40" />
                    <div className="h-2.5 bg-gray-100 rounded-full w-24" />
                  </div>
                  <div className="h-4 bg-gray-100 rounded-full w-20" />
                </div>
              ))}
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <ReceiptPercentIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No expenses found</p>
              <p className="text-xs mt-1">Add your first expense using the button above</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {expenses.map((exp) => {
                const ci = catInfo(exp.category);
                const label = exp.category === 'custom' ? exp.customCategory : ci.label;
                return (
                  <div key={exp._id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors group">
                    {/* Category icon */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                      style={{ background: `${ci.color}18` }}>
                      {ci.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-800 truncate">{exp.title}</p>
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: `${ci.color}18`, color: ci.color }}>
                          {ci.icon} {label}
                        </span>
                        {exp.batchId && (
                          <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-2 py-0.5 font-medium">
                            📋 {exp.batchId.name}
                          </span>
                        )}
                        {exp.courseId && (
                          <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-full px-2 py-0.5 font-medium">
                            🎓 {exp.courseId.name}
                          </span>
                        )}
                        {exp.staffId && (
                          <span className="text-[10px] bg-violet-50 text-violet-600 border border-violet-200 rounded-full px-2 py-0.5 font-medium">
                            👤 {exp.staffId.name}
                          </span>
                        )}
                        {exp.trainerId && (
                          <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full px-2 py-0.5 font-medium">
                            🎓 {exp.trainerId.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1"><CalendarDaysIcon className="h-3 w-3" />{fmtDate(exp.date)}</span>
                        <span className="capitalize">{PAYMENT_METHODS.find(m => m.value === exp.paymentMethod)?.label || exp.paymentMethod}</span>
                        {exp.description && <span className="truncate max-w-[200px]">{exp.description}</span>}
                        {exp.paymentSlip && (
                          <a href={exp.paymentSlip} target="_blank" rel="noreferrer"
                            className="flex items-center gap-0.5 text-indigo-500 hover:underline">
                            <PaperClipIcon className="h-3 w-3" /> Slip
                          </a>
                        )}
                        {exp.createdBy && <span>By {exp.createdBy.name || exp.createdBy.email}</span>}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-gray-800">{fmt(exp.amount)}</p>
                    </div>

                    {/* Actions (show on hover) */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => openEdit(exp)}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteId(exp._id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ CHARTS TAB ══ */}
      {activeTab === 'charts' && (
        <div className="space-y-6">
          {statsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1,2].map(i => <div key={i} className="h-72 bg-white rounded-2xl border border-gray-100 animate-pulse" />)}
            </div>
          ) : !stats ? (
            <div className="text-center py-16 text-gray-400">No data available</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category breakdown (pie) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <TagIcon className="h-4 w-4 text-violet-500" /> Expenses by Category
                </h3>
                {stats.byCategory.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No data</p>
                ) : (
                  <div className="flex flex-col md:flex-row gap-4 items-center">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={stats.byCategory} dataKey="total" nameKey="label" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                          {stats.byCategory.map((entry) => {
                            const ci = catInfo(entry.category);
                            return <Cell key={entry.category} fill={ci.color} />;
                          })}
                        </Pie>
                        <Tooltip formatter={(v) => fmt(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-col gap-1.5 min-w-[150px]">
                      {stats.byCategory.map(c => {
                        const ci = catInfo(c.category);
                        return (
                          <div key={c.category} className="flex items-center gap-2 text-xs">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ci.color }} />
                            <span className="text-gray-600 truncate max-w-[110px]">{ci.label}</span>
                            <span className="ml-auto font-semibold text-gray-800">{fmt(c.total)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Monthly trend (bar) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <ChartBarIcon className="h-4 w-4 text-violet-500" /> Monthly Trend
                </h3>
                {stats.byMonth.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No data</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stats.byMonth} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Bar dataKey="total" fill="#7c3aed" radius={[4,4,0,0]} name="Total" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Category table */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 lg:col-span-2">
                <h3 className="font-semibold text-gray-700 mb-4">Category Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="font-semibold text-xs uppercase tracking-wider">Category</th>
                        <th className="font-semibold text-xs uppercase tracking-wider text-right">Entries</th>
                        <th className="font-semibold text-xs uppercase tracking-wider text-right">Total Amount</th>
                        <th className="font-semibold text-xs uppercase tracking-wider text-right">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {stats.byCategory.map(c => {
                        const ci  = catInfo(c.category);
                        const pct = stats.totalAmount ? ((c.total / stats.totalAmount) * 100).toFixed(1) : 0;
                        return (
                          <tr key={c.category} className="hover:bg-gray-50">
                            <td className="py-2.5 flex items-center gap-2">
                              <span className="text-lg">{ci.icon}</span>
                              <span>{ci.label}</span>
                            </td>
                            <td className="py-2.5 text-right text-gray-500">{c.count}</td>
                            <td className="py-2.5 text-right font-semibold">{fmt(c.total)}</td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ci.color }} />
                                </div>
                                <span className="text-gray-500 text-xs w-8 text-right">{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 border-gray-200 font-bold">
                        <td className="py-2.5">Total</td>
                        <td className="py-2.5 text-right">{stats.totalCount}</td>
                        <td className="py-2.5 text-right text-violet-700">{fmt(stats.totalAmount)}</td>
                        <td className="py-2.5 text-right">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Filter Panel (modal) ── */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setFilterOpen(false)} />
          <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-600 to-indigo-600">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-white" />
                <span className="text-base font-semibold text-white">Filters</span>
              </div>
              <button onClick={() => setFilterOpen(false)} className="text-white/80 hover:text-white">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">Search</label>
                <div className="relative mt-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Search by title or description…"
                    value={draft.search}
                    onChange={e => setDraft(d => ({ ...d, search: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Category</label>
                  <select
                    value={draft.category}
                    onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  >
                    <option value="all">All Categories</option>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Batch</label>
                  <select
                    value={draft.batchId}
                    onChange={e => setDraft(d => ({ ...d, batchId: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  >
                    <option value="all">All Batches</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">From Date</label>
                  <input type="date" value={draft.startDate}
                    onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">To Date</label>
                  <input type="date" value={draft.endDate}
                    onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                onClick={() => { setDraft(BLANK_DRAFT); setApplied(BLANK_DRAFT); setFilterOpen(false); }}
              >
                ↺ Clear All
              </button>
              <button
                className="text-sm px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold transition-colors"
                onClick={() => { setApplied({ ...draft }); setFilterOpen(false); }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Dialog ── */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !saving && setDialogOpen(false)} />
          <div className="relative z-10 w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <ReceiptPercentIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">{editId ? 'Edit Expense' : 'Add New Expense'}</h2>
                  <p className="text-xs text-violet-200 mt-0.5">Fill in the expense details below</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto popup-scroll flex-1 px-6 py-5 space-y-4">
              {/* Title */}
              <div>
                <label className="text-xs font-semibold text-gray-600">Title <span className="text-red-500">*</span></label>
                <input
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="e.g., July Electricity Bill, Staff Salary June"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-semibold text-gray-600">Category <span className="text-red-500">*</span></label>
                <div className="mt-1 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, category: c.value }))}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 text-xs font-medium transition-all ${
                        form.category === c.value
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-gray-100 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <span className="text-xl">{c.icon}</span>
                      <span className="text-center leading-tight">{c.label}</span>
                    </button>
                  ))}
                </div>
                {form.category === 'custom' && (
                  <input
                    className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    placeholder="Custom category name *"
                    value={form.customCategory}
                    onChange={e => setForm(f => ({ ...f, customCategory: e.target.value }))}
                  />
                )}
              </div>

              {/* Amount + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Amount (₹) <span className="text-red-500">*</span></label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">₹</span>
                    <input
                      type="number" min="0" step="0.01"
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="0"
                      value={form.amount}
                      onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="text-xs font-semibold text-gray-600">Payment Method</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {PAYMENT_METHODS.map(m => (
                    <button
                      key={m.value} type="button"
                      onClick={() => setForm(f => ({ ...f, paymentMethod: m.value }))}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                        form.paymentMethod === m.value
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-semibold text-gray-600">Description / Notes</label>
                <textarea
                  rows={2}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                  placeholder="Optional notes or details…"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>

              {/* Staff selector — only for Salaries */}
              {form.category === 'salaries' && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-3.5 space-y-1.5">
                  <p className="text-xs font-semibold text-violet-700 flex items-center gap-1.5">
                    👤 Staff Member <span className="font-normal text-violet-500">(optional)</span>
                  </p>
                  <select
                    value={form.staffId}
                    onChange={e => setForm(f => ({ ...f, staffId: e.target.value }))}
                    className="w-full border border-violet-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  >
                    <option value="">— Select staff member —</option>
                    {staffList.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.name} ({s.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Trainer selector — only for Trainer Payments */}
              {form.category === 'trainer' && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3.5 space-y-1.5">
                  <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                    🎓 Trainer <span className="font-normal text-indigo-500">(optional)</span>
                  </p>
                  <select
                    value={form.trainerId}
                    onChange={e => setForm(f => ({ ...f, trainerId: e.target.value }))}
                    className="w-full border border-indigo-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  >
                    <option value="">— Select trainer —</option>
                    {trainers.map(t => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Batch + Course (optional) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Linked Batch <span className="text-gray-400">(optional)</span></label>
                  <select
                    value={form.batchId}
                    onChange={e => {
                      const selectedBatch = batches.find(b => b._id === e.target.value);
                      setForm(f => ({
                        ...f,
                        batchId:  e.target.value,
                        // Auto-fill course from the selected batch
                        courseId: selectedBatch?.courseId?._id || selectedBatch?.courseId || f.courseId,
                      }));
                    }}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  >
                    <option value="">— No batch —</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Linked Course <span className="text-gray-400">(optional)</span></label>
                  <select
                    value={form.courseId}
                    onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                  >
                    <option value="">— No course —</option>
                    {courses.map(c => <option key={c._id} value={c._id}>{c.courseName || c.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Payment slip */}
              <div>
                <label className="text-xs font-semibold text-gray-600">Payment Slip <span className="text-gray-400">(optional)</span></label>
                {form.paymentSlip ? (
                  <div className="mt-1 flex items-center gap-2 p-2.5 border border-green-200 bg-green-50 rounded-lg">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <a href={form.paymentSlip} target="_blank" rel="noreferrer"
                      className="text-xs text-indigo-600 hover:underline truncate flex-1">
                      View uploaded slip
                    </a>
                    <button type="button" onClick={() => setForm(f => ({ ...f, paymentSlip: '' }))}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => slipInputRef.current?.click()}
                    disabled={slipUploading}
                    className="mt-1 w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg py-3 text-sm text-gray-500 hover:border-violet-400 hover:text-violet-600 transition-colors"
                  >
                    {slipUploading ? (
                      <><div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-violet-500 animate-spin" /> Uploading…</>
                    ) : (
                      <><DocumentArrowUpIcon className="h-4 w-4" /> Upload payment slip / receipt</>
                    )}
                  </button>
                )}
                <input
                  ref={slipInputRef} type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={e => e.target.files?.[0] && handleSlipUpload(e.target.files[0])}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 flex-shrink-0">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <><div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" /> Saving…</>
                ) : editId ? 'Update Expense' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !deleting && setDeleteId(null)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-800">Delete Expense?</h3>
              <p className="text-sm text-gray-500">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center justify-center gap-2">
                {deleting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
